import React, { useEffect } from 'react';
import { usePlugins } from './usePlugins';
import { Plugin } from './types';

export interface PluginSlotProps {
  children?: React.ReactNode;
  /** The name of the slot to render plugins for */
  slotName: string;
  /** Layout mode for plugins */
  layout?: 'stack' | 'grid' | 'flex';
  /** Grid columns when layout is 'grid' */
  gridColumns?: number;
  /** Filter function to conditionally render plugins */
  filter?: (plugin: Plugin) => boolean;
  /** Sort function to order plugins */
  sort?: (a: Plugin, b: Plugin) => number;
  /** Custom CSS class for the slot container */
  className?: string;
  /** Custom styles for the slot container */
  style?: React.CSSProperties;
  /** Error handler function */
  onError?: (error: Error, pluginName: string) => void;
  /** Props to pass to all plugin components */
  sharedProps?: Record<string, any>;
  /** Custom error component when a plugin fails */
  errorComponent?: React.ComponentType<{ error: Error; pluginName: string }>;
  /** Custom empty component when no plugins are found */
  emptyComponent?: React.ComponentType<{ slotName: string }>;
  /** Whether to also execute script plugins in this slot */
  executeScripts?: boolean;
}

const DefaultErrorComponent: React.FC<{ error: Error; pluginName: string }> = ({ 
  error, 
  pluginName 
}) => (
  <div style={{ 
    background: '#ffebee', 
    border: '1px solid #f44336', 
    padding: '8px', 
    borderRadius: '4px',
    color: '#c62828',
    fontSize: '14px'
  }}>
    <strong>⚠️ Plugin Error: {pluginName}</strong>
    <div style={{ fontSize: '12px', marginTop: '4px' }}>{error.message}</div>
  </div>
);

const DefaultEmptyComponent: React.FC<{ slotName: string }> = ({ slotName }) => (null);

const SkeletonLoader: React.FC<{ layout?: 'stack' | 'grid' | 'flex'; count?: number }> = ({ 
  layout = 'stack', 
  count = 2 
}) => {
  const skeletonStyle: React.CSSProperties = {
    backgroundColor: '#f0f0f0',
    borderRadius: '8px',
    animation: 'pulse 1.5s ease-in-out infinite alternate',
    marginBottom: layout === 'stack' ? '12px' : '0'
  };

  const containerStyle: React.CSSProperties = {
    display: layout === 'stack' ? 'block' : layout === 'grid' ? 'grid' : 'flex',
    gap: '12px',
    gridTemplateColumns: layout === 'grid' ? 'repeat(2, 1fr)' : undefined,
    flexWrap: layout === 'flex' ? 'wrap' : undefined
  };

  return (
    <>
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          100% { opacity: 0.5; }
        }
      `}</style>
      <div style={containerStyle}>
        {Array.from({ length: count }, (_, i) => (
          <div
            key={i}
            style={{
              ...skeletonStyle,
              height: Math.random() * 40 + 60, // Random height between 60-100px
              width: layout === 'flex' ? Math.random() * 100 + 150 + 'px' : '100%'
            }}
          />
        ))}
      </div>
    </>
  );
};

export const PluginSlot: React.FC<PluginSlotProps> = ({
  children,
  slotName,
  layout = 'stack',
  gridColumns = 2,
  filter,
  sort,
  className,
  style,
  onError,
  sharedProps = {},
  errorComponent: ErrorComponent = DefaultErrorComponent,
  emptyComponent: EmptyComponent = DefaultEmptyComponent,
  executeScripts = true
}) => {
  const { plugins } = usePlugins(slotName);
  
  // Debug: Log what plugins we have for this slot
  console.log(`PluginSlot "${slotName}" - Found ${plugins.length} plugins:`, plugins.map(p => ({
    name: p.meta.name,
    hasComponent: !!p.Component,
    hasSlots: !!p.slots,
    slotComponents: p.slots?.[slotName]?.components ? Object.keys(p.slots[slotName].components) : []
  })));

  // Apply filtering - render both legacy and slot-based plugins
  let filteredPlugins = plugins.filter(plugin => {
    // Check for legacy Component
    const hasLegacyComponent = plugin.Component && (plugin.meta.type !== 'script');
    
    // Check for slot-based components
    const hasSlotComponents = plugin.slots?.[slotName]?.components && 
                             Object.keys(plugin.slots[slotName].components).length > 0;
    
    const hasAnyComponent = hasLegacyComponent || hasSlotComponents;
    
    if (filter) {
      return hasAnyComponent && filter(plugin);
    }
    
    return hasAnyComponent;
  });

  // Apply sorting
  if (sort) {
    filteredPlugins = filteredPlugins.sort(sort);
  }

  // Handle empty state
  if (filteredPlugins.length === 0) {
    return (
      <div className={className} style={style}>
        {children || <EmptyComponent slotName={slotName} />}
      </div>
    );
  }

  // Calculate layout styles
  const getLayoutStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      display: layout === 'stack' ? 'block' : layout === 'grid' ? 'grid' : 'flex',
      gap: '12px',
      ...style
    };

    if (layout === 'grid') {
      baseStyles.gridTemplateColumns = `repeat(${gridColumns}, 1fr)`;
    } else if (layout === 'flex') {
      baseStyles.flexWrap = 'wrap';
    }

    return baseStyles;
  };

  const renderPlugin = (plugin: Plugin, index: number) => {
    const { Component, meta, slots } = plugin;
    
    // Prepare props with children for plugin components
    const pluginProps = {
      ...sharedProps,
      children: children, // Pass original slot children to plugin components
      slotName: slotName  // Also pass the slot name for context
    };
    
    // Handle slot-based components first
    if (slots?.[slotName]?.components) {
      const slotComponents = slots[slotName].components;
      return Object.entries(slotComponents).map(([componentName, ComponentFunc]) => {
        try {
          return (
            <div key={`${meta.name}-${componentName}`} className={`plugin-${meta.name}-${componentName}`}>
              <ComponentFunc {...pluginProps} />
            </div>
          );
        } catch (error) {
          const err = error as Error;
          
          // Call onError callback if provided
          if (onError) {
            onError(err, `${meta.name}.${componentName}`);
          }

          // Render error component
          return (
            <div key={`error-${meta.name}-${componentName}`}>
              <ErrorComponent error={err} pluginName={`${meta.name}.${componentName}`} />
            </div>
          );
        }
      });
    }
    
    // Handle legacy Component
    if (Component) {
      try {
        return (
          <div key={meta.name || index} className={`plugin-${meta.name}`}>
            <Component {...pluginProps} />
          </div>
        );
      } catch (error) {
        const err = error as Error;
        
        // Call onError callback if provided
        if (onError) {
          onError(err, meta.name);
        }

        // Render error component
        return (
          <div key={`error-${meta.name || index}`}>
            <ErrorComponent error={err} pluginName={meta.name} />
          </div>
        );
      }
    }
    
    // Skip if no component
    return null;
  };

  return (
    <div 
      className={className}
      style={getLayoutStyles()}
    >
      {filteredPlugins.map((plugin, index) => renderPlugin(plugin, index)).flat()}
    </div>
  );
};
